import {
  useState, useRef, useEffect, useContext,
} from 'react';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  useLiveAgentSession, useDrawerPrefs, createActionId, getDefaultSession,
  shouldResetSession, reconcileSession, sessionRecord, drawerPrefsRecord,
} from '../platform/session/session';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { emit } from '../platform/events/lifecycle';
import { emitHeaderEntryClick } from '../shared/analytics';
import { HEADER_ENTRY } from '../shared/assistant-config';

/**
 * The Shell's eager half: the drawer state machine and FAB behavior, in the main chunk so both
 * work before the assistant content loads. See .claude/context/session-identity.md.
 */
export const useDrawerController = () => {
  const { isConsumerApp, customer } = useContext(ExperienceContext);
  // Cross-READ only; liveAgent block is the sole writer. See .claude/context/session-identity.md
  const [liveAgent] = useLiveAgentSession();
  // update() merges onto the record's current value; neither writer can revert the other.
  const [drawerPrefs, updatePrefs] = useDrawerPrefs();
  const { lesser: isMobile } = useBreakpoint('lg');
  // Do not use this setter directly, use updateDrawerState instead.
  const [drawerState, setDrawerState] = useState('CLOSED');
  // Lazy-init so the channel is created once, not re-allocated (and leaked) on every render.
  const broadcastChannel = useRef(null);
  if (!broadcastChannel.current) {
    broadcastChannel.current = createBroadcastChannel(CHANNELS.assistant);
  }
  // Mini drawer is disabled for B2B customers and while escalated to an agent (design decision).
  const enableMiniDrawer = customer?.type !== 'b2b' && !liveAgent?.isEscalated;

  const updateDrawerState = (newDrawerState, { skipBroadcast } = {}) => {
    setDrawerState(newDrawerState);
    if (!skipBroadcast) {
      // App opens new tabs instead of reloading; keep drawer state in sync across app tabs.
      broadcastChannel.current.postMessage({
        type: 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED',
        data: { drawerState: newDrawerState }
      });
    }
  };

  const handleOpen = (isUserInteraction = false, origin = HEADER_ENTRY.ORIGIN_FAB) => {
    const isFromHeader = origin === HEADER_ENTRY.ORIGIN_HEADER;

    if (isUserInteraction && isFromHeader) emitHeaderEntryClick(HEADER_ENTRY.OPEN_LABEL);

    updateDrawerState('OPEN');
    // Read the prior flag before the update, so the fab-click event still fires on the first
    // interaction of the session.
    const alreadyInteracted = drawerPrefsRecord.get()?.hasSessionInteractedWithFab;
    updatePrefs({
      hasSessionInteractedWithFab: alreadyInteracted || isUserInteraction
    });
    // The cadences differ: analytics counts every header ask, fab-click stays once per
    // session. Do not fold them back together.
    if (isUserInteraction && !isFromHeader && !alreadyInteracted) {
      const { sessionId } = sessionRecord.get() ?? {};
      emit('magic-apron-assistant.fab-click', {
        actionID: createActionId(sessionId),
        sessionID: sessionId,
      });
    }
  };

  // Opens this tab directly and broadcasts to peers (deliberate; user-confirmed 2026-07-27).
  // See .claude/context/composition-topology.md and .claude/context/session-identity.md
  const openDrawer = () => {
    handleOpen(false);
    broadcastChannel.current.postMessage({ type: 'MAA_OPEN_DRAWER' });
  };

  // Closing must NOT announce `isReady: false` — see .claude/context/event-bus.md.
  // `origin` is compared strictly since this is also passed straight to a click-event `onClose`.
  const handleClose = (origin = HEADER_ENTRY.ORIGIN_FAB) => {
    if (origin === HEADER_ENTRY.ORIGIN_HEADER) emitHeaderEntryClick(HEADER_ENTRY.CLOSE_LABEL);

    updateDrawerState('CLOSED');
    updatePrefs({ shouldAutoOpen: false });
  };

  // A drag-to-open is a user interaction, so OPEN routes through handleOpen(true).
  const handleDrawerStateChange = (newDrawerState) => {
    if (newDrawerState === 'CLOSED') handleClose();
    if (newDrawerState === 'OPEN') handleOpen(true);
    if (newDrawerState === 'MINIMIZED') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
      updatePrefs({ shouldAutoOpen: true });
    }
  };

  // Host asks a question on the user's behalf → remember to auto-open next time.
  const markShouldAutoOpen = () => updatePrefs({ shouldAutoOpen: true });

  useEffect(() => {
    // Seed, reconcile, then maybe reset — mount-only. See .claude/context/session-identity.md
    sessionRecord.seed();

    const reconciled = reconcileSession(sessionRecord.get());
    if (reconciled?.sessionId && reconciled.sessionId !== sessionRecord.get()?.sessionId) {
      sessionRecord.set(reconciled);
    }

    const willReset = shouldResetSession(sessionRecord.get());
    const outgoingPrefs = drawerPrefsRecord.get();
    const shouldAutoOpen = willReset
      ? outgoingPrefs.shouldAutoOpen !== false
      : outgoingPrefs?.shouldAutoOpen;

    if (willReset) {
      // maa-live-agent is deliberately NOT reset here — single-writer rule, see live-agent.md
      sessionRecord.set(getDefaultSession());
      drawerPrefsRecord.set({
        shouldAutoOpen,
        hasSessionInteractedWithFab: false,
        pagesExpandedFABShownOn: [],
      });
    }

    if (shouldAutoOpen) {
      let autoOpenState = 'OPEN';
      if (isMobile) autoOpenState = enableMiniDrawer ? 'MINIMIZED' : 'CLOSED';
      updateDrawerState(autoOpenState);
    }

    // Handles only drawer traffic; product pods emit their own analytics (see session-identity.md).
    broadcastChannel.current.onmessage = ({ data: { type, data } }) => {
      if (type === 'MAA_OPEN_DRAWER') {
        handleOpen(false);
      } else if (type === 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED' && isConsumerApp) {
        // Cross-tab drawer state synchronization for the mobile app.
        updateDrawerState(data.drawerState, { skipBroadcast: true });
      }
    };

    return () => {
      broadcastChannel.current.close();
    };
  }, []);

  useEffect(() => {
    emit('magic-apron-assistant.drawer-minimize', drawerState);
  }, [drawerState]);

  // On tablets, changing orientation can flip the layout between open and minimized.
  useEffect(() => {
    if (isMobile && drawerState === 'OPEN') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
    }
    if (!isMobile && drawerState === 'MINIMIZED') {
      updateDrawerState('OPEN');
    }
  }, [isMobile]);

  return {
    drawerState,
    handleOpen,
    handleClose,
    handleDrawerStateChange,
    markShouldAutoOpen,
    openDrawer,
  };
};
